import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { onSessionExpired } from "./session";
import { LoginPayload, Profile } from "./models";
import { login as loginApi, logout as logoutApi } from "./authApi";
import { clearCredentials, loadCredentials, saveCredentials } from "./tokenStorage";

interface AuthContextValue {
  profile: Profile | null;
  sessionExpiredReason: string | null;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(() => loadCredentials()?.profile ?? null);
  const [sessionExpiredReason, setSessionExpiredReason] = useState<string | null>(null);

  useEffect(() => {
    const subscription = onSessionExpired((reason) => {
      setProfile(null);
      setSessionExpiredReason(reason);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = useCallback(async (payload: LoginPayload) => {
    const credentials = await loginApi(payload);
    saveCredentials(credentials);
    setSessionExpiredReason(null);
    setProfile(credentials.profile);
  }, []);

  const logout = useCallback(async () => {
    setSessionExpiredReason(null);
    setProfile(null);
    await logoutApi();
    clearCredentials();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ profile, sessionExpiredReason, login, logout }),
    [profile, sessionExpiredReason, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
